import { compact } from 'lodash-es';
import { InvoiceDisplayStatusV2, InvoiceSortingColumn, PaymentDetailType, ISSUES_PREVENTING_PAYMENT } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { invoiceSummaryModel } from '~/models';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { getFormattedTimeRange } from './DateFilterPicker';
import { invoiceDisplayStatusLabels } from './invoicesTableFilters';
export var InvoiceHeaderItemID = /*#__PURE__*/ function(InvoiceHeaderItemID) {
    InvoiceHeaderItemID["invoiceId"] = "invoiceId";
    InvoiceHeaderItemID["companyName"] = "companyName";
    InvoiceHeaderItemID["status"] = "status";
    InvoiceHeaderItemID["issueDate"] = "issueDate";
    InvoiceHeaderItemID["dueDate"] = "dueDate";
    InvoiceHeaderItemID["total"] = "total";
    InvoiceHeaderItemID["additionalInformation"] = "additionalInformation";
    InvoiceHeaderItemID["paymentMethod"] = "paymentMethod";
    return InvoiceHeaderItemID;
}({});
export const invoiceHeaderItemIdToSortingColumn = {
    ["invoiceId"]: InvoiceSortingColumn.Number,
    ["companyName"]: InvoiceSortingColumn.CompanyName,
    ["status"]: InvoiceSortingColumn.Status,
    ["issueDate"]: InvoiceSortingColumn.IssuedOn,
    ["dueDate"]: InvoiceSortingColumn.DueDate,
    ["total"]: InvoiceSortingColumn.TotalAmount
};
export function getInvoiceStatusFilterOptions(param) {
    let { counters, texts } = param;
    const labels = invoiceDisplayStatusLabels(texts);
    return [
        InvoiceDisplayStatusV2.Issued,
        InvoiceDisplayStatusV2.Pending,
        InvoiceDisplayStatusV2.Overdue,
        InvoiceDisplayStatusV2.Processing,
        InvoiceDisplayStatusV2.Paid,
        InvoiceDisplayStatusV2.Voided,
        InvoiceDisplayStatusV2.Refunded,
        InvoiceDisplayStatusV2.RefundFailed,
        InvoiceDisplayStatusV2.Disputed,
        InvoiceDisplayStatusV2.PartiallyRefunded
    ].map((statusId)=>({
            value: statusId,
            label: labels[statusId],
            counter: counters[statusId] || 0
        })).filter((opt)=>opt.counter);
}
export function getIssuesPreventingPaymentFilterOptions(param) {
    let { counters, texts } = param;
    return ISSUES_PREVENTING_PAYMENT.map((type)=>({
            value: type,
            label: getIssuePreventingPaymentLabel(type, texts),
            counter: counters[type] || 0
        })).filter((opt)=>opt.counter);
}
function getIssuePreventingPaymentLabel(type, texts) {
    switch(type){
        case PaymentDetailType.MissingPaymentMethod:
            return texts.missingPaymentMethod();
        case PaymentDetailType.ClientApprovalRequired:
            return texts.clientApprovalRequired();
        case PaymentDetailType.PaymentFailed:
            return texts.paymentFailed();
        case PaymentDetailType.PaymentValidationRequired:
            return texts.paymentValidationRequired();
        case PaymentDetailType.PaymentStopped:
            return texts.paymentStopped();
        case PaymentDetailType.PaymentMethodNotVerified:
            return texts.paymentMethodNotVerified();
        default:
            return '';
    }
}
export function getProcessedByFilterOptions(param) {
    let { counters, texts } = param;
    return [
        {
            value: 'anchor',
            label: texts.anchor(),
            counter: counters[PaymentDetailType.PaidOnDate] || 0
        },
        {
            value: 'external',
            label: texts.external(),
            counter: counters[PaymentDetailType.PaidExternally] || 0
        }
    ].filter((opt)=>opt.counter);
}
export function getAccountingStatusFilterOptions(param) {
    let { counters, texts } = param;
    const syncedCounter = counters.sync || 0;
    const unsyncedCounter = counters.unsync || 0;
    return [
        {
            value: AccountingStatusFilter.All,
            label: texts.all(),
            counter: syncedCounter + unsyncedCounter
        },
        {
            value: AccountingStatusFilter.Synced,
            label: texts.synced(),
            counter: syncedCounter
        },
        {
            value: AccountingStatusFilter.NotSynced,
            label: texts.notSynced(),
            counter: unsyncedCounter
        }
    ].filter((opt)=>opt.counter);
}
export function applyOptimisticUpdate(invoices, optimisticUpdate, now) {
    const { type, invoiceId } = optimisticUpdate;
    const updateInvoice = (update)=>invoices.map((invoice)=>invoice.id === invoiceId ? update(invoice) : invoice);
    switch(type){
        case InvoiceOptimisticUpdateType.Refunded:
            return updateInvoice((inv)=>invoiceSummaryModel.setRefunded(inv, optimisticUpdate.timestamp, now));
        case InvoiceOptimisticUpdateType.RefundRequested:
            return updateInvoice((inv)=>invoiceSummaryModel.setRefundRequested(inv));
        case InvoiceOptimisticUpdateType.PartialRefundRequested:
            return updateInvoice((inv)=>invoiceSummaryModel.setPartialRefundRequested(inv));
        case InvoiceOptimisticUpdateType.Voided:
            return updateInvoice((inv)=>invoiceSummaryModel.setVoided(inv));
        case InvoiceOptimisticUpdateType.PaymentApproved:
            return updateInvoice((inv)=>invoiceSummaryModel.setPending(inv, now));
        case InvoiceOptimisticUpdateType.PaymentConfirmed:
            return updateInvoice((inv)=>invoiceSummaryModel.setPaymentConfirmed(inv, optimisticUpdate.isVendor));
        case InvoiceOptimisticUpdateType.QboIntegrationChanged:
            return updateInvoice((inv)=>invoiceSummaryModel.setIntegration(inv, optimisticUpdate.integration));
        case InvoiceOptimisticUpdateType.XeroIntegrationChanged:
            return updateInvoice((inv)=>invoiceSummaryModel.setXeroIntegration(inv, optimisticUpdate.xeroIntegration));
        case InvoiceOptimisticUpdateType.PartiallyRefunded:
            return updateInvoice((inv)=>invoiceSummaryModel.setPartiallyRefunded(inv, optimisticUpdate.timestamp));
        case InvoiceOptimisticUpdateType.Changed:
            {
                const { invoice } = optimisticUpdate;
                return updateInvoice((_inv)=>invoice);
            }
    }
}
export function mapSortAndFiltersRequestParams(param) {
    let { date, now, sort, sortIds, search, displayStatusV2, qbo, xero, paymentDetails } = param;
    const { fromDate, toDate } = getFormattedTimeRange(date, now);
    return {
        search,
        displayStatusV2,
        paymentDetails: paymentDetails && paymentDetails.length > 0 ? paymentDetails : undefined,
        integration: qbo,
        xeroIntegration: xero,
        issuedOnSince: fromDate ? new Date(fromDate) : undefined,
        issuedOnUntil: toDate ? new Date(toDate) : undefined,
        sort: sort ? invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]] ? {
            field: invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]],
            type: sort.sort
        } : undefined : undefined
    };
}
export function getTableDefs(param) {
    let { showQboIntegration, hideDueDate, showXeroIntegration, texts, AD_HOC_INVOICING } = param;
    const issueDateLabel = texts.issueDate();
    const totalLabel = texts.total();
    const tableDefs = compact([
        {
            headerItem: {
                id: "invoiceId",
                value: texts.invoiceId()
            },
            columnDef: {
                width: showQboIntegration || showXeroIntegration ? 140 : 100,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_invoice_id'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "companyName",
                value: texts.companyName()
            },
            columnDef: {
                width: [
                    150,
                    200
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_company_name'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "status",
                value: texts.invoiceStatus()
            },
            columnDef: {
                width: [
                    96,
                    140
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_invoice_status'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "additionalInformation",
                value: texts.paymentDetails()
            },
            columnDef: {
                width: [
                    160,
                    220
                ],
                sortable: false
            },
            defaultSortDirection: 'asc'
        },
        AD_HOC_INVOICING && {
            headerItem: {
                id: "paymentMethod",
                value: texts.paymentMethod()
            },
            columnDef: {
                width: [
                    100,
                    140
                ],
                sortable: false
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "issueDate",
                value: issueDateLabel
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                bi: {
                    name: 'sort_issue_date'
                },
                mobile: {
                    label: {
                        text: issueDateLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        !hideDueDate && {
            headerItem: {
                id: "dueDate",
                value: texts.dueDate()
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                bi: {
                    name: 'sort_due_date'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "total",
                value: totalLabel
            },
            columnDef: {
                width: 84,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_total'
                },
                mobile: {
                    label: {
                        text: totalLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        }
    ]);
    const headerItems = tableDefs.map((d)=>d.headerItem);
    const columnDefs = tableDefs.map((d)=>d.columnDef);
    const sortIds = headerItems.map((item)=>item.id);
    const sortOptions = tableDefs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
            {
                id: d.headerItem.id,
                label: String(d.headerItem.value),
                defaultDirection: d.defaultSortDirection,
                colIndex
            }
        ] : []);
    return {
        columnDefs,
        headerItems,
        sortIds,
        sortOptions
    };
}
